import React from 'react';

/** Per employee: why they are not available in the planned slot (see server/services/assigneeAvailability.ts). */
export type EmployeeAvailability = {
  absence: string | null;
  busy: string | null;
};

export type AssigneeAvailabilityMap = Record<string, EmployeeAvailability>;

/** „Urlaub“, „Gebucht: „Baustelle“ 09:00–11:00“ or both; undefined when the employee is free. */
export function availabilityHint(availability: EmployeeAvailability | undefined): string | undefined {
  if (!availability) return undefined;
  return [availability.absence, availability.busy].filter(Boolean).join(' · ') || undefined;
}

/** Short badge text for the picker row („Urlaub“, „Gebucht“), full hint as tooltip. */
export type AvailabilityBadge = { label: string; detail: string };

export function availabilityBadge(availability: EmployeeAvailability | undefined): AvailabilityBadge | undefined {
  const detail = availabilityHint(availability);
  if (!availability || !detail) return undefined;
  const label = availability.absence
    ? availability.absence.split(', ')[0].replace(/\s*\(.*\)$/, '')
    : 'Gebucht';
  return { label, detail };
}

/** Selected employees that are absent or booked, for the day preview. */
export function unavailableAssignees(
  availability: AssigneeAvailabilityMap,
  employeeIds: string[],
  nameOf: (id: string) => string,
): { id: string; name: string; hint: string }[] {
  return employeeIds.flatMap((id) => {
    const hint = availabilityHint(availability[id]);
    return hint ? [{ id, name: nameOf(id), hint }] : [];
  });
}

/**
 * Loads who is absent or already booked in the planned slot. Debounced so typing a time
 * does not fire a request per keystroke; returns {} while loading or on errors — the
 * save-time check on the server stays the safety net.
 */
export function useAssigneeAvailability(params: {
  dueAt: string | null;
  endAt: string | null;
  excludeActivityId?: string | null;
  enabled?: boolean;
}): AssigneeAvailabilityMap {
  const { dueAt, endAt, excludeActivityId, enabled = true } = params;
  const [availability, setAvailability] = React.useState<AssigneeAvailabilityMap>({});

  React.useEffect(() => {
    if (!enabled || !dueAt || Number.isNaN(new Date(dueAt).getTime())) {
      setAvailability({});
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({ due_at: dueAt });
      if (endAt) query.set('end_at', endAt);
      if (excludeActivityId) query.set('exclude_activity_id', excludeActivityId);
      fetch(`/api/activities/assignee-availability?${query}`, {
        credentials: 'include',
        signal: controller.signal,
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { employees?: AssigneeAvailabilityMap } | null) => {
          setAvailability(data?.employees ?? {});
        })
        .catch(() => {
          if (!controller.signal.aborted) setAvailability({});
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [dueAt, endAt, excludeActivityId, enabled]);

  return availability;
}
